import React, { useEffect, useState } from 'react';
import { Button, Modal, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  useDismissReleaseNote,
  useLatestReleaseNote,
} from '@/api/ReleaseNotes';
import { useAppCustomers, useHttpAuthReady, useIdTokenClaims } from '@/hooks';
import { useTranslate } from '@/translations';
import { AppRoutes } from '@/routes';
import dayjs from 'dayjs';

export const ReleaseNotesPopup: React.FC = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { hasLoggedIn } = useIdTokenClaims();
  const isHttpAuthReady = useHttpAuthReady();
  const {
    userPermissions: { loaded },
  } = useAppCustomers();
  const enabled = hasLoggedIn && loaded && isHttpAuthReady;
  const { data, isError } = useLatestReleaseNote({ enabled });
  const { mutateAsync: dismiss } = useDismissReleaseNote();
  const [open, setOpen] = useState(false);

  const note = data?.note;

  useEffect(() => {
    setOpen(Boolean(enabled && !isError && note));
  }, [enabled, isError, note]);

  if (!note || isError || !enabled) {
    return null;
  }

  const close = () => {
    setOpen(false);
    dismiss(note.versionLabel).catch(() => undefined);
  };

  const viewHistory = () => {
    close();
    navigate(AppRoutes.releaseNotes);
  };

  return (
    <Modal
      open={open}
      title={note.title || t('releaseNotesPopupTitle')}
      onCancel={close}
      footer={
        <Space>
          <Button onClick={viewHistory}>{t('releaseNotesViewHistory')}</Button>
          <Button type="primary" onClick={close}>
            {t('releaseNotesDismiss')}
          </Button>
        </Space>
      }
      width={720}
    >
      <p>
        {note.versionLabel} - {note.title}
        {note.publishedAt
          ? ` · ${dayjs(note.publishedAt).format('MMM D')}`
          : ''}
      </p>
      <div
        dangerouslySetInnerHTML={{
          __html: note.notes,
        }}
      />
    </Modal>
  );
};
